import React from 'react';

import { TaskResponse } from '../../client';
import { useMemoryMiniGame } from '../../hooks';

import { TaskCard } from './TaskCard';

interface TaskCardMemoryGameProps {
  task: TaskResponse;
  onTaskStarted: (task: TaskResponse) => Promise<void>;
  onTaskProgress: (task: TaskResponse, progress: number) => void;
  onTaskCompleted: (task: TaskResponse) => Promise<void>;
}

export function TaskCardMemoryGame({ task, onTaskStarted, onTaskProgress, onTaskCompleted }: TaskCardMemoryGameProps) {
  const { cards, revealCard } = useMemoryMiniGame(task, onTaskStarted, onTaskProgress, onTaskCompleted);

  return (
    <TaskCard task={task}>
      <div className="grid grid-cols-4 gap-3">
        {cards.map((card, index) => (
          <div key={index}>
            <div
              className="max-w-[18vw] w-[100px] max-h-[18vw] h-[100px]"
              style={{ cursor: !card.isRevealed && !card.isMatched ? 'pointer' : 'default' }}
              onClick={() => revealCard(index)}
            >
              <MemoryGameCard content={card.content} revealed={card.isRevealed || card.isMatched} />
            </div>
          </div>
        ))}
      </div>
    </TaskCard>
  );
}

interface CardProps {
  content: string;
  revealed: boolean;
}

const MemoryGameCard: React.FC<CardProps> = ({ content, revealed }) => {
  return (
    <div
      className="w-full h-full cursor-pointer"
      style={{ perspective: '1000px', WebkitTapHighlightColor: 'transparent', outline: 'none' }}
    >
      <div
        className="w-full h-full relative transition-transform duration-[600ms]"
        style={{
          transformStyle: 'preserve-3d',
          transform: revealed ? 'rotateY(0deg)' : 'rotateY(180deg)',
        }}
      >
        <MemoryGameCardSide front>
          <span className="text-4xl font-bold text-feedback-error">{content}</span>
        </MemoryGameCardSide>

        <MemoryGameCardSide>
          <img src="/card-back.jpg" alt="Card back" className="w-full h-full object-cover rounded-[10px]" />
        </MemoryGameCardSide>
      </div>
    </div>
  );
};

interface SquareCardProps {
  children: React.ReactNode;
  front?: boolean;
}

const MemoryGameCardSide: React.FC<SquareCardProps> = ({ children, front }) => {
  return (
    <div
      className="w-full h-full absolute flex items-center justify-center text-center border-2 border-[#EBD5E0] rounded-xl shadow-md"
      style={{
        background: 'linear-gradient(135deg, #E3CCF3, #70D860, #E3CCF3)',
        transform: front ? 'rotateY(0deg)' : 'rotateY(180deg)',
        backfaceVisibility: 'hidden',
      }}
    >
      {children}
    </div>
  );
};
